import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IoSettings, IoCalendar } from 'react-icons/io5';
import { IoMdFunnel, IoMdHeartHalf, IoIosMail } from 'react-icons/io';
import { IconType } from 'react-icons';
import { SettingsPage, FiltersPage, SwipePage, InboxPage, DatesPage } from '../pages';
import './WaveNavigation.css';
import { useUI } from '../context/UIContext';

interface NavigationItem {
  id: string;
  icon: IconType;
  title: string;
  color: string;
  component: React.ComponentType<any>;
}

const navigationItems: NavigationItem[] = [
  {
    id: 'settings',
    icon: IoSettings,
    title: 'Settings',
    color: 'linear-gradient(to top, #09203f 0%, #537895 100%)',
    component: SettingsPage
  },
  {
    id: 'filters',
    icon: IoMdFunnel,
    title: 'Filters',
    color: '#ff6b81',
    component: FiltersPage
  },
  {
    id: 'swipe',
    icon: IoMdHeartHalf,
    title: 'Swipe!',
    color: '#7bed9f',
    component: SwipePage
  },
  {
    id: 'inbox',
    icon: IoIosMail,
    title: 'Inbox',
    color: '#70a1ff',
    component: InboxPage
  },
  {
    id: 'dates',
    icon: IoCalendar,
    title: 'Dates',
    color: '#dfe4ea',
    component: DatesPage
  }
];

const WaveNavigation: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number>(2);
  const waveRef = useRef<SVGSVGElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const waveWrapRef = useRef<HTMLDivElement>(null);
  const { showWave, setShowWave } = useUI();
  const [navToken, setNavToken] = useState<number>(0);
  const [activeId, setActiveId] = useState<string>(navigationItems[2].id);

  // Align wave position and background using actual element sizes
  const alignWave = useCallback((index: number) => {
    if (!waveRef.current || !listRef.current || !waveWrapRef.current || !bodyRef.current) {
      return;
    }

    const items = listRef.current.querySelectorAll('li');
    if (!items || index < 0 || index >= items.length) {
      return;
    }

    const liRect = items[index].getBoundingClientRect();
    const wrapRect = waveWrapRef.current.getBoundingClientRect();
    const waveRect = waveRef.current.getBoundingClientRect();

    const centerX = liRect.left + liRect.width / 2;
    const newLeft = centerX - waveRect.width / 2 - wrapRect.left;
    waveRef.current.style.left = `${newLeft}px`;

    // Set background color
    bodyRef.current.style.background = navigationItems[index].color;
  }, []);

  // Handle navigation item click
  const handleItemClick = useCallback((index: number) => {
    setActiveIndex(index);
    alignWave(index);
    // Ensure wave and active state are restored when navigating away from chat
    setShowWave(true);
    setActiveId(navigationItems[index].id);
    setNavToken((t) => t + 1);
  }, [alignWave, setShowWave]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.keyCode === 9) { // Tab key
        e.preventDefault();
        const nextIndex = activeIndex === navigationItems.length - 1 ? 0 : activeIndex + 1;
        handleItemClick(nextIndex);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, handleItemClick]);

  // Initialize and keep wave aligned on mount and when layout changes
  useEffect(() => {
    const realign = () => alignWave(activeIndex);
    // Realign after initial render
    realign();
    window.addEventListener('resize', realign);
    window.addEventListener('orientationchange', realign);
    return () => {
      window.removeEventListener('resize', realign);
      window.removeEventListener('orientationchange', realign);
    };
  }, [activeIndex, alignWave]);

  const CurrentPageComponent = navigationItems[activeIndex].component;

  return (
    <div ref={bodyRef} className="wave-navigation-container">
      <div className="phone">
        <div className="page-content-area">
          <CurrentPageComponent navId={activeId} navToken={navToken} />
        </div>
        
        <nav className="wave-nav">
          <div className={`wave-wrap ${showWave ? '' : 'hidden'}`} ref={waveWrapRef}>
            <svg
              ref={waveRef}
              id="wave"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 119 26"
            >
              <path
                className="path"
                d="M120.8,26C98.1,26,86.4,0,60.4,0C35.9,0,21.1,26,0.5,26H120.8z"
              />
            </svg>
          </div>

          <ul className="list-wrap" ref={listRef}>
            {navigationItems.map((item, index) => {
              const IconComponent = item.icon as React.ComponentType<any>;
              return (
                <li
                  key={item.id}
                  className={index === activeIndex && showWave ? 'active' : ''}
                  onClick={() => handleItemClick(index)}
                  title={item.title}
                >
                  <i className="icon">
                    <IconComponent />
                  </i>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default WaveNavigation;
